/**
 * fieldDisplay — plain-language rendering of suggested-edit field names +
 * values for the admin review surfaces (X.6.7A).
 *
 * The suggestion wire shape carries raw column names (`phone_2`,
 * `dementia_unit`, `is_active`) + raw values (`'true'`, `'false'`, null).
 * The review UI must NOT surface those system tokens (X.6 design brief §
 * Pending). These helpers map a field to its operator-facing label and a
 * value to a readable form.
 *
 * The label maps mirror SuggestEditForm's ORG_FIELD_LABELS /
 * INDIVIDUAL_FIELD_LABELS (the picker dropdown's source of truth). Kept
 * in lockstep by hand; the snake_case fallback covers any field not yet
 * mapped so nothing ever renders as a raw token even if the maps drift.
 */

import type { SuggestedEditTargetType } from '@/lib/types';
import { formatPostalParts, postalPartsOf } from '@/lib/address';

const ORG_FIELD_LABELS: Record<string, string> = {
  address_split: 'Address split into its parts',
  name: 'Name',
  phone: 'Phone',
  email: 'Email',
  address: 'Address',
  website: 'Website',
  is_active: 'Remove from directory',
  medicaid: 'Medicaid acceptance',
  dementia_unit: 'Secure Dementia Unit',
  phone_2: 'Secondary phone',
  fax: 'Fax',
  payment_types: 'Payment types',
  service_delivery_model: 'Delivery method',
  gender_restrictions: 'Gender restrictions',
  // Item 5 — M:N junction suggestable fields (delta-applied).
  subcategories: 'Subcategories',
  specialties: 'Specialties',
};

// Item 5 — org fields whose suggested-edit value is JSON, not a scalar:
// either a `{ add, remove }` slug DELTA (suggested_edits) or an absolute
// slug array (change_log / timeline). humanizeFieldValue renders these
// readably and NEVER leaks raw JSON to users.
const ORG_JUNCTION_FIELD_NAMES = new Set<string>(['subcategories', 'specialties']);

const INDIVIDUAL_FIELD_LABELS: Record<string, string> = {
  address_split: 'Address split into its parts',
  full_name: 'Full name',
  title: 'Title',
  business_phone: 'Business phone',
  cell_phone: 'Cell phone',
  email: 'Email',
  address: 'Address',
  specialties: 'Specialties',
  notes: 'Notes',
  is_active: 'Remove from directory',
};

/** snake_case → "Sentence case" fallback for any unmapped field. */
function titleizeSnake(field: string): string {
  return field.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

/**
 * Operator-facing label for a suggested-edit field. Maps known columns to
 * their picker label; falls back to a titleized snake_case form so a raw
 * token never reaches the screen.
 */
export function humanizeFieldName(
  targetType: SuggestedEditTargetType,
  field: string,
): string {
  const map =
    targetType === 'individual' ? INDIVIDUAL_FIELD_LABELS : ORG_FIELD_LABELS;
  return map[field] ?? titleizeSnake(field);
}

/**
 * Optional context for {@link humanizeFieldValue}. When `targetType` +
 * `fieldName` identify an org junction field (Subcategories / Specialties),
 * the value is rendered as a readable add/remove (or list) instead of raw
 * JSON. `labelFor` resolves a slug → display name when the caller has the
 * lookups loaded; absent it, the slug itself is shown (still readable,
 * never JSON). `targetType` is loose (string) so AdminTimelineTargetType
 * callers pass through without a cast.
 */
export interface HumanizeValueOpts {
  targetType?: string;
  fieldName?: string | null;
  labelFor?: (slug: string) => string;
}

/**
 * Readable rendering of a suggested-edit / change-log value. Empty/null →
 * "(empty)"; the boolean wire tokens 'true' / 'false' → "Yes" / "No".
 *
 * Item 5 — for org junction fields (Subcategories / Specialties) the value
 * is JSON: a `{ add, remove }` slug delta (suggested_edits) OR an absolute
 * slug array (change_log). This shared renderer is used by ALL suggestion
 * surfaces — steward review, "My suggestions", list rows, and the activity
 * timeline — so it must render every shape readably and never leak JSON.
 *
 * The bare 1-arg call is unchanged for every existing (scalar) field.
 */
export function humanizeFieldValue(
  value: string | null | undefined,
  opts?: HumanizeValueOpts,
): string {
  if (
    opts?.targetType === 'organization' &&
    opts.fieldName != null &&
    ORG_JUNCTION_FIELD_NAMES.has(opts.fieldName)
  ) {
    return humanizeJunctionValue(value, opts.labelFor);
  }
  if (value === null || value === undefined || value === '') return '(empty)';
  // A Directory address held as its parts' JSON reads as its text.
  if (opts?.fieldName === 'address') {
    const parts = postalPartsOf(value);
    if (parts !== null) return formatPostalParts(parts);
  }
  if (value === 'true') return 'Yes';
  if (value === 'false') return 'No';
  return value;
}

/**
 * Render a junction value (delta object OR absolute array). Falls back to
 * the raw string on any non-JSON or unexpected shape — it must never throw
 * and never surface a `{"add":[...]}` token to a user.
 */
function humanizeJunctionValue(
  value: string | null | undefined,
  labelFor?: (slug: string) => string,
): string {
  if (value === null || value === undefined || value === '') return '(none)';
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    return value;
  }
  const label = (slug: string): string => labelFor?.(slug) ?? slug;
  const asStrings = (v: unknown): string[] =>
    Array.isArray(v) ? v.filter((s): s is string => typeof s === 'string') : [];

  // Delta shape: { add, remove }.
  if (
    typeof parsed === 'object' &&
    parsed !== null &&
    !Array.isArray(parsed) &&
    ('add' in parsed || 'remove' in parsed)
  ) {
    const obj = parsed as { add?: unknown; remove?: unknown };
    const add = asStrings(obj.add);
    const remove = asStrings(obj.remove);
    const addStr = add.length > 0 ? add.map(label).join(', ') : '(none)';
    const removeStr = remove.length > 0 ? remove.map(label).join(', ') : '(none)';
    return `Adding: ${addStr} · Removing: ${removeStr}`;
  }
  // Absolute slug array (change_log before/after).
  if (Array.isArray(parsed)) {
    const slugs = asStrings(parsed);
    return slugs.length > 0 ? slugs.map(label).join(', ') : '(none)';
  }
  return value;
}
